import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Type, Edit3, Minus } from 'react-feather';
import './SignatureSelector.scss';

export type SignatureMethod = 'text' | 'placeholder' | 'digital';

export interface SignatureData {
  method: SignatureMethod;
  textSignature?: string;
  digitalSignature?: string;
}

export interface SignatureSelectorProps {
  patientName: string;
  onSignatureComplete: (data: SignatureData) => void;
  onCancel?: () => void;
  initialMethod?: SignatureMethod;
}

interface SignaturePadProps {
  onSignatureChange: (dataUrl: string | null) => void;
}

function SignaturePad({ onSignatureChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getCoordinates = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ('touches' in e) {
        const touch = e.touches[0];
        if (!touch) return null;
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  const startDrawing = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const coords = getCoordinates(e);
      if (!coords) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      setIsDrawing(true);
    },
    [getCoordinates]
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if (!isDrawing) return;
      const coords = getCoordinates(e);
      if (!coords) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      setHasDrawn(true);
    },
    [isDrawing, getCoordinates]
  );

  const stopDrawing = useCallback(() => {
    if (isDrawing && hasDrawn) {
      const canvas = canvasRef.current;
      if (canvas) {
        onSignatureChange(canvas.toDataURL('image/png'));
      }
    }
    setIsDrawing(false);
  }, [isDrawing, hasDrawn, onSignatureChange]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignatureChange(null);
  }, [onSignatureChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad__canvas"
        width={600}
        height={200}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="signature-pad__actions">
        <button
          type="button"
          className="signature-pad__clear-btn"
          onClick={clearCanvas}
          disabled={!hasDrawn}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

export function SignatureSelector({
  patientName,
  onSignatureComplete,
  onCancel,
  initialMethod = 'text',
}: SignatureSelectorProps) {
  const [method, setMethod] = useState<SignatureMethod>(initialMethod);
  const [textValue, setTextValue] = useState(patientName);
  const [digitalSignature, setDigitalSignature] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    const data: SignatureData = { method };

    switch (method) {
      case 'text':
        data.textSignature = textValue;
        break;
      case 'digital':
        data.digitalSignature = digitalSignature || undefined;
        break;
      case 'placeholder':
        break;
    }

    onSignatureComplete(data);
  }, [method, textValue, digitalSignature, onSignatureComplete]);

  const isSubmitDisabled = useCallback(() => {
    switch (method) {
      case 'text':
        return !textValue.trim();
      case 'digital':
        return !digitalSignature;
      case 'placeholder':
        return false;
      default:
        return true;
    }
  }, [method, textValue, digitalSignature]);

  return (
    <div className="signature-selector">
      <div className="signature-selector__header">
        <h3 className="signature-selector__title">Select Signature Method</h3>
        {onCancel && (
          <button
            type="button"
            className="signature-selector__close-btn"
            onClick={onCancel}
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="signature-selector__options">
        <button
          type="button"
          className={`signature-selector__option ${method === 'text' ? 'selected' : ''}`}
          onClick={() => setMethod('text')}
        >
          <Type size={20} />
          <span>Type Signature</span>
        </button>
        <button
          type="button"
          className={`signature-selector__option ${method === 'placeholder' ? 'selected' : ''}`}
          onClick={() => setMethod('placeholder')}
        >
          <Minus size={20} />
          <span>Placeholder</span>
        </button>
        <button
          type="button"
          className={`signature-selector__option ${method === 'digital' ? 'selected' : ''}`}
          onClick={() => setMethod('digital')}
        >
          <Edit3 size={20} />
          <span>Draw Signature</span>
        </button>
      </div>

      <div className="signature-selector__content">
        {method === 'text' && (
          <div className="signature-selector__text-input">
            <p className="signature-selector__method-hint">
              Type your name below to generate a signature.
            </p>
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder="Type your name"
              className="signature-selector__input"
            />
            <div className="signature-selector__preview">
              <span className="signature-selector__text-preview">
                {textValue || 'Your Signature'}
              </span>
            </div>
          </div>
        )}

        {method === 'placeholder' && (
          <div className="signature-selector__placeholder">
            <div className="signature-selector__placeholder-line">
              <span>(SIGNATURE)</span>
            </div>
            <p className="signature-selector__method-hint">
              A signature placeholder will be added to the document.
            </p>
          </div>
        )}

        {method === 'digital' && (
          <div className="signature-selector__digital">
            <p className="signature-selector__method-hint">
              Use your finger or stylus to draw your signature.
            </p>
            <SignaturePad onSignatureChange={setDigitalSignature} />
          </div>
        )}
      </div>

      <div className="signature-selector__actions">
        {onCancel && (
          <button
            type="button"
            className="signature-selector__cancel-btn"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="signature-selector__submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitDisabled()}
        >
          Apply Signature
        </button>
      </div>
    </div>
  );
}

export default SignatureSelector;
